<script setup>
import { computed, ref, toRef, watch } from "vue";
import ImagePreviewModal from "components/jiffy/vue-cart/CartOrder/ImagePreviewModal.vue";
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { useCompositePreview } from "components/jiffy/vue-cart/LineItem/useCompositePreview";

const props = defineProps({
  lineItem: {
    type: Object,
    required: true
  },
  compositePreview: {
    type: Object,
    default: null
  },
  productDetails: {
    type: Object,
    default: null
  },
  selectedColor: {
    type: Object,
    default: null
  }
});

const formatPrintSize = size => {
  const width = parseFloat(size.width).toFixed(2);
  const height = parseFloat(size.height).toFixed(2);
  return `${width}" x ${height}"`;
};

const currentLineItem = computed(() => ({
  ...props.lineItem,
  productSize: props.lineItem.printSizes?.[0] ? formatPrintSize(props.lineItem.printSizes[0]) : null
}));

const imagePreviewModal = ref(null);

const brand = computed(() => currentLineItem.value.brand || {});
const emit = defineEmits(["is-artworks-loading"]);

const compositePreviewRef = toRef(props, "compositePreview");
const { frontImage, backImage, previewIsLoading, previewError } =
  useCompositePreview(compositePreviewRef);

const previews = computed(() => [frontImage.value, backImage.value].filter(Boolean));

const colorName = computed(
  () => props.selectedColor?.colorName ?? currentLineItem.value.productColor
);
const imageStates = ref({
  front: { isLoading: true, hasError: false },
  back: { isLoading: true, hasError: false }
});

// Determine if we should render/load the back image based on available surfaces
const shouldShowBack = computed(() => {
  const placements = compositePreviewRef.value?.placements || [];
  if (Array.isArray(placements) && placements.some(p => p?.position === "back")) return true;

  const sides = props.productDetails?.surfaceMeta?.sides;
  if (sides == null) return true;

  const backSides = sides?.back;
  const backSupported = Array.isArray(backSides) && backSides.length > 0;
  return backSupported;
});

const frontImageUrl = computed(
  () => frontImage.value || props.selectedColor?.surfaces?.front?.picture
);
const backImageUrl = computed(
  () => backImage.value || props.selectedColor?.surfaces?.back?.picture
);

const isArtworksLoading = computed(() => {
  const frontLoading =
    previewIsLoading.value || !frontImageUrl.value || imageStates.value.front.isLoading;

  if (shouldShowBack.value) {
    return frontLoading || !backImageUrl.value || imageStates.value.back.isLoading;
  }

  return frontLoading;
});

const getOverlayText = position => {
  if (imageStates.value[position].hasError) {
    return "Error loading image";
  }
  if (previewError.value) {
    return "Error loading artwork";
  }
  return "Loading...";
};

const handleImageClick = () => {
  imagePreviewModal.value.open();
};

const handleImageLoad = position => {
  imageStates.value[position].isLoading = false;
};

const handleImageError = position => {
  imageStates.value[position].hasError = true;
};

watch(frontImageUrl, () => {
  imageStates.value.front = { isLoading: true, hasError: false };
});

watch(backImageUrl, () => {
  imageStates.value.back = { isLoading: true, hasError: false };
});

watch(isArtworksLoading, newValue => {
  emit("is-artworks-loading", newValue);
});
</script>

<template>
  <div class="product-details">
    <div class="product-details__images">
      <div class="product-details__image-wrapper">
        <button type="button" class="product-details__zoom" @click="handleImageClick">
          <UseSvg class="product-details__zoom-icon" icon="search" />
        </button>
        <img
          :alt="`${currentLineItem.productName} - Front`"
          :src="frontImageUrl"
          class="product-details__image"
          @load="handleImageLoad('front')"
          @error="handleImageError('front')"
        />
        <div v-if="isArtworksLoading" class="product-details__image-loading">
          <span class="product-details__image-loading-text">{{ getOverlayText("front") }}</span>
        </div>
      </div>
      <div v-if="shouldShowBack" class="product-details__image-wrapper">
        <img
          :alt="`${currentLineItem.productName} - Back`"
          :src="backImageUrl"
          class="product-details__image"
          @load="handleImageLoad('back')"
          @error="handleImageError('back')"
        />
        <div v-if="isArtworksLoading" class="product-details__image-loading">
          <span class="product-details__image-loading-text">{{ getOverlayText("back") }}</span>
        </div>
      </div>
    </div>

    <div class="product-details__info">
      <div class="product-details__brand">
        <img
          v-if="brand.logo"
          :alt="`${brand.name} logo`"
          :src="brand.logo"
          class="product-details__brand-logo"
        />
        <span class="product-details__style">
          {{ currentLineItem.productStyle }}
        </span>
      </div>

      <h3 class="product-details__name">{{ currentLineItem.productName }}</h3>

      <ul class="product-details__specs">
        <li class="product-details__spec">
          <span class="product-details__spec-label">Color:</span>
          <span class="product-details__spec-value">
            {{ colorName }}
          </span>
        </li>

        <li
          v-for="size in currentLineItem.printSizes"
          :key="size.position"
          class="product-details__spec"
        >
          <span class="product-details__spec-label">Design {{ size.position }}:</span>
          <span class="product-details__spec-value">
            {{ formatPrintSize(size) }}
          </span>
        </li>
      </ul>
    </div>
    <Teleport to="body">
      <ImagePreviewModal
        ref="imagePreviewModal"
        should-disable-edit
        :line-item="currentLineItem"
        :previews="previews"
      />
    </Teleport>
  </div>
</template>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.product-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;

  @include with-tablet {
    flex-direction: row;
  }

  &__images {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  &__info {
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 5px;
  }

  &__brand {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    position: absolute;
    right: 0;

    @include with-tablet {
      position: relative;
      align-items: flex-start;
    }
  }

  &__brand-logo {
    width: auto;
    height: 12px;
    object-fit: contain;
  }

  &__style {
    font-size: 12px;
    font-weight: 700;
    color: $warm-grey;
  }

  &__name {
    color: $black;
    font-size: 16px;
    line-height: 1;
    font-weight: 700;
    max-width: 80%;

    @include with-tablet {
      max-width: 100%;
    }
  }

  &__specs {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__spec {
    font-size: 14px;
    line-height: 1.4;
    display: inline-block;
    margin-right: 20px;

    @include with-tablet {
      display: block;
      margin-bottom: 5px;
    }
  }

  &__spec-label {
    margin-right: 4px;
    color: $warm-grey;
    font-weight: 400;
  }

  &__spec-value {
    color: $text-black;
    font-weight: 700;
  }

  &__image-wrapper {
    position: relative;
    width: 50%;
    height: 245px;
    border-radius: 9px;
    overflow: hidden;
    border: 3px solid $gray-border;

    @include with-tablet {
      width: 190px;
      height: 275px;
    }
  }

  &__image {
    width: 170px;
    height: 245px;
    flex-shrink: 0;
    object-fit: cover;

    @include with-tablet {
      width: 190px;
      height: 275px;
    }
  }

  &__image-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: $white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__image-loading-text {
    color: $warm-grey;
    font-size: 12px;
    font-weight: 700;
  }

  &__zoom {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 4px;
    left: 4px;
    width: 18px;
    height: 18px;
    border-radius: 2px;
    background-color: $border-blue-light;

    @include with-desktop {
      top: 8px;
      left: 8px;
      width: 36px;
      height: 36px;
      border-radius: 4px;
    }
  }

  &__zoom-icon {
    width: 12px;
    height: 12px;
    color: $white;

    @include with-desktop {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
